import React, { useMemo, useState } from "react";
import { FieldError } from "react-hook-form";
import { Calendar, Day, DayRange } from "@hassanmojab/react-modern-calendar-datepicker";
import { mdiCalendar, mdiClockOutline } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import Input from "components/Input";
import Modal from "components/Modal";
import { getClassByVariant } from "helpers/getClassByVariant";
import styles from "./style.module.scss";
import "@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css";
import "./_style.scss";

interface IProps {
  value?: string;
  onChange?: (value: string) => void;
  label?: string | React.ReactNode;
  className?: string;
  labelClass?: string;
  inputClass?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: FieldError;
  color?: "primary" | "secondary";
  isRange?: boolean;
  required?: boolean;
  showTimePicker?: boolean;
  minimumDate?: string;
  clearable?: boolean;
}

interface ICalendarFooter {
  onClose: VoidFunction;
}

const dayString = ({ day, month, year }: Day) =>
  dayjs(`${month}/${day}/${year}`).format("MM/DD/YYYY");

const toDay = (value: string): Day => {
  const date = dayjs(value);
  return { day: date.date(), month: date.month() + 1, year: date.year() };
};

const timeString = (timeValue: { hours: number; minutes: number }) =>
  `${timeValue.hours.toString().padStart(2, "0")}:${timeValue.minutes.toString().padStart(2, "0")}`;

function CalendarFooter({ onClose }: Readonly<ICalendarFooter>) {
  return (
    <div className="flex flex-1 justify-between *:h-10 *:w-24 *:text-sm m-6 mx-10 -mt-6">
      <button type="button" onClick={onClose}>
        Cancel
      </button>
      <Button type="button" onClick={onClose}>
        Done
      </Button>
    </div>
  );
}

function RangeCalendar({
  dateValue,
  onChange,
  onSubmit,
  minimumDate
}: Readonly<{
  dateValue: any;
  onChange: (value: any) => void;
  onSubmit: VoidFunction;
  minimumDate?: Day;
}>) {
  return (
    <Calendar
      value={dateValue}
      onChange={onChange}
      minimumDate={minimumDate}
      colorPrimary="rgb(var(--primary))"
      colorPrimaryLight="rgb(var(--primary))"
      slideAnimationDuration="0.5s"
      calendarTodayClassName="!text-primary underline underline-offset-4"
      renderFooter={() => <CalendarFooter onClose={onSubmit} />}
    />
  );
}

function SingleDateCalendar({
  dayValue,
  onChange,
  onSubmit,
  minimumDate
}: Readonly<{
  dayValue: any;
  onChange: (value: any) => void;
  onSubmit: VoidFunction;
  minimumDate?: Day;
}>) {
  return (
    <Calendar
      value={dayValue}
      onChange={onChange}
      minimumDate={minimumDate}
      colorPrimary="rgb(var(--primary))"
      colorPrimaryLight="rgb(var(--primary))"
      slideAnimationDuration="0.5s"
      calendarTodayClassName="!text-primary underline underline-offset-4"
      renderFooter={() => <CalendarFooter onClose={onSubmit} />}
    />
  );
}

export default function DatePicker({
  value,
  onChange,
  label,
  placeholder,
  color = "primary",
  className,
  labelClass,
  error,
  inputClass,
  disabled = false,
  isRange = false,
  required = false,
  showTimePicker = false,
  minimumDate,
  clearable = false
}: Readonly<IProps>) {
  const inputDate = value ? dayjs(value) : null;
  const minimumDay = minimumDate ? toDay(minimumDate) : undefined;
  const [isOpen, toggleModal] = useToggle(false);
  const [dayValue, setDayValue] = useState(
    inputDate
      ? {
          day: inputDate.date(),
          month: inputDate.month() + 1,
          year: inputDate.year()
        }
      : undefined
  );
  const [dateValue, setDateValue] = useState({
    from: null,
    to: null
  });

  const [timeValue, setTimeValue] = useState({
    hours: inputDate ? inputDate.hour() : dayjs().hour(),
    minutes: inputDate ? inputDate.minute() : dayjs().minute()
  });

  const inputValue = useMemo(() => {
    if (isRange) {
      const dayRange = dateValue as DayRange;
      if (dayRange.from && dayRange.to) {
        return showTimePicker
          ? `${dayString(dayRange.from)} ${timeString(timeValue)} to ${dayString(dayRange.to)} ${timeString(timeValue)}`
          : `${dayString(dayRange.from)} to ${dayString(dayRange.to)}`;
      }
    } else if (dayValue) {
      return showTimePicker
        ? `${dayString(dayValue)} ${timeString(timeValue)}`
        : dayString(dayValue);
    }
    return "";
  }, [dateValue, dayValue, timeValue]);

  const onSubmit = () => {
    toggleModal();
    onChange && onChange(inputValue);
  };

  return (
    <div className={clsx(styles.smartDatePickerWrapper, className)}>
      {label && (
        <label
          className={clsx(
            styles.smartDatePickerLabel,
            getClassByVariant(color)?.label,
            labelClass
          )}>
          {required ? "* " : ""}
          {label}
        </label>
      )}

      <Input
        aria-label={typeof label === "string" ? label : undefined}
        value={value}
        onChange={(event) => onChange && onChange(event.target.value)}
        onClick={toggleModal}
        className={clsx(
          styles.smartDatePickerInput,
          getClassByVariant(color)?.input,
          !!error && "!border-b-error"
        )}
        inputClass={inputClass}
        placeholder={placeholder}
        readOnly
        disabled={disabled}
      />
      <Icon
        path={mdiCalendar}
        className={clsx(styles.calendarIcon, showTimePicker ? "!right-10" : "!right-3")}
      />
      {showTimePicker ? <Icon path={mdiClockOutline} className={styles.clockIcon} /> : null}

      {error && <span className="text-sm leading-5 text-error">{error.message}</span>}
      {clearable && value && !disabled ? (
        <button
          type="button"
          className="absolute top-0 right-0 text-sm leading-none text-primary underline underline-offset-4"
          onClick={() => {
            setDayValue(undefined);
            onChange && onChange("");
          }}>
          Clear date
        </button>
      ) : null}
      <Modal
        isOpen={isOpen}
        closeModal={toggleModal}
        wrapperClass="!m-auto !p-0"
        dialogClass="desktop:!m-auto"
        closeClass="!hidden">
        <div>
          {isRange ? (
            <RangeCalendar
              dateValue={dateValue}
              onChange={setDateValue}
              onSubmit={onSubmit}
              minimumDate={minimumDay}
            />
          ) : (
            <SingleDateCalendar
              dayValue={dayValue}
              onChange={setDayValue}
              onSubmit={onSubmit}
              minimumDate={minimumDay}
            />
          )}

          {showTimePicker ? (
            <div className="flex justify-center mt-1">
              <Input
                type="time"
                className="w-full max-w-xs mobile:text-sm !text-text"
                inputClass="text-text !bg-foreground !py-3"
                value={timeString(timeValue)}
                onChange={(e) => {
                  const timeStr = e.target.value;
                  if (timeStr) {
                    const [hours, minutes] = timeStr.split(":");
                    setTimeValue({
                      hours: parseInt(hours, 10) || 0,
                      minutes: parseInt(minutes, 10) || 0
                    });
                  }
                }}
              />
            </div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
